import cn from 'classnames';
import _isEqual from 'lodash/isEqual';
import { nanoid } from 'nanoid';
import React, { memo, useState, useCallback, useEffect } from 'react';
import { Skeleton } from '@sravni/react-design-system/lib/Skeleton';
import { useBoolean, usePrevious } from '@sravni/react-utils';
import { DEFAULT_HAS_PLACEHOLDER, BANNER_TYPES_WITH_PLACEHOLDERS } from '../../constants/banner.js';
import { decorateParams } from '../../utils/decorateParams.js';
import queue from '../../utils/queue.js';
import { loadYandexAdsContextOnce, initBanner } from './helpers.js';
import modules_efc4e723 from './styles.module.scss.js';

// Yandex Banner
const AdFox = memo(({ banner, bannerClassName, placeholderClassName, lazy = false, abName, type, 
// @ts-expect-error
hasPlaceholder = DEFAULT_HAS_PLACEHOLDER[type], onError, onRender, onClick, ownerId, ...rest }) => {
    const [isContainerReady, setIsContainerReady] = useBoolean(false);
    const [containerId, setContainerId] = useState();
    const [isBannerLoaded, setBannerLoaded] = useState();
    const [isError, setError] = useBoolean(false);
    const [isLoadedByYandex, setLoadedByYandex] = useBoolean(false);
    const [isBannerRendered, setBannerRenderStatus] = useBoolean(false);
    const decoratedParams = banner?.params ? decorateParams(banner, abName) : {};
    const containerRef = useCallback((node) => {
        if (node) {
            setIsContainerReady.on();
        }
    }, [setIsContainerReady]);
    const prevBanner = usePrevious(banner);
    useEffect(() => {
        if (_isEqual(banner, prevBanner))
            return;
        const id = nanoid();
        setContainerId(id);
        setBannerLoaded(false);
        setError.off();
        setLoadedByYandex.off();
        setBannerRenderStatus.off();
    }, [banner, prevBanner, setContainerId, setBannerLoaded, setError, setLoadedByYandex, setBannerRenderStatus]);
    const handleError = (error) => {
        setError.on();
        onError(JSON.stringify(error));
    };
    const handleRender = () => {
        setBannerRenderStatus.on();
        onRender?.();
    };
    const handleLoad = () => {
        setLoadedByYandex.on();
    };
    const handleStub = () => {
        handleError({ type: 'stub', message: `В adfox нет подходящего баннера` });
    };
    useEffect(() => {
        if (!isContainerReady || !containerId || isBannerLoaded || !banner)
            return;
        setBannerLoaded(true);
        queue.push(async () => {
            await loadYandexAdsContextOnce().then(setError.off).catch(setError.on);
            initBanner({
                lazy,
                containerId,
                decoratedParams,
                onError: handleError,
                onLoad: handleLoad,
                onRender: handleRender,
                onStub: handleStub,
                ownerId,
            });
        });
        return () => {
            window.yaContextCb = window.yaContextCb || [];
            window.yaContextCb.push(() => {
                // @ts-expect-error Cannot find name Ya
                Ya.adfoxCode.destroy({ containerId });
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isContainerReady, containerId, isBannerLoaded, banner, handleError, handleLoad, handleStub, ownerId]);
    const withPlaceholder = hasPlaceholder && BANNER_TYPES_WITH_PLACEHOLDERS.includes((banner?.bannerType || type));
    return (React.createElement("div", { ...rest },
        React.createElement("div", { onClick: onClick },
            React.createElement("div", { id: containerId, key: containerId, ref: containerRef, className: cn(modules_efc4e723.container, {
                    [modules_efc4e723.container_video]: banner?.isVideo && isLoadedByYandex && !isBannerRendered,
                }, bannerClassName) })),
        withPlaceholder && !isLoadedByYandex && !isError && (React.createElement(Skeleton.Button, { block: true, className: cn(modules_efc4e723.placeholder, 'adfox_placeholder', modules_efc4e723[`placeholder_${banner?.bannerType || type}`], {
                [modules_efc4e723.placeholder_video]: banner?.isVideo,
            }, placeholderClassName) }))));
});
AdFox.displayName = 'AdFox';

export { AdFox };
//# sourceMappingURL=index.js.map
